<template>
  <NavDrawer />

  <!-- Hero Section -->
  <v-container fluid class="pa-0">
    <v-sheet color="primary-darken-1" class="py-12">
      <v-container>
        <v-row justify="center">
          <v-col cols="12" md="10" lg="8" class="text-center">
            <h1 class="text-h3 text-white font-weight-bold mb-4">
              Find Your Perfect Cemetery Space
            </h1>
            <p class="text-h6 text-white text-medium-emphasis">
              Browse available cemetery plots and memorial spaces from trusted sellers
            </p>
          </v-col>
        </v-row>
      </v-container>
    </v-sheet>
  </v-container>

  <!-- Search & Filter Section -->
  <v-container class="mt-n8">
    <v-row justify="center">
      <v-col cols="12" md="10" lg="8">
        <v-card elevation="8" rounded="lg">
          <v-card-text class="pa-6">
            <v-row align="center" class="mb-2">
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="filters.cemeteryname"
                  label="Cemetery Name"
                  prepend-inner-icon="mdi-office-building-marker"
                  variant="outlined"
                  density="comfortable"
                  hide-details
                  clearable
                  @keyup.enter="handleEnterKey"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="filters.city"
                  label="City"
                  prepend-inner-icon="mdi-city"
                  variant="outlined"
                  density="comfortable"
                  hide-details
                  clearable
                  @keyup.enter="handleEnterKey"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="filters.state"
                  label="State"
                  prepend-inner-icon="mdi-map-marker"
                  variant="outlined"
                  density="comfortable"
                  hide-details
                  clearable
                  @keyup.enter="handleEnterKey"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="filters.county"
                  label="County"
                  prepend-inner-icon="mdi-map"
                  variant="outlined"
                  density="comfortable"
                  hide-details
                  clearable
                  @keyup.enter="handleEnterKey"
                />
              </v-col>
            </v-row>
            
            <!-- Active Filters -->
            <v-row v-if="hasActiveFilters" class="mt-2">
              <v-col cols="12">
                <v-chip-group>
                  <v-chip
                    v-if="filters.cemeteryname"
                    closable
                    @click:close="filters.cemeteryname = ''"
                    color="primary"
                    variant="outlined"
                    size="small"
                  >
                    Cemetery: {{ filters.cemeteryname }}
                  </v-chip>
                  <v-chip
                    v-if="filters.city"
                    closable
                    @click:close="filters.city = ''"
                    color="primary"
                    variant="outlined"
                    size="small"
                  >
                    City: {{ filters.city }}
                  </v-chip>
                  <v-chip
                    v-if="filters.state"
                    closable
                    @click:close="filters.state = ''"
                    color="primary"
                    variant="outlined"
                    size="small"
                  >
                    State: {{ filters.state }}
                  </v-chip>
                  <v-chip
                    v-if="filters.county"
                    closable
                    @click:close="filters.county = ''"
                    color="primary"
                    variant="outlined"
                    size="small"
                  >
                    County: {{ filters.county }}
                  </v-chip>
                </v-chip-group>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>

  <!-- Results Section -->
  <v-container id="listings" class="mt-8">
    <v-row>
      <v-col cols="12">
        <div class="d-flex align-center justify-space-between mb-4 flex-wrap gap-3">
          <div>
            <h2 class="text-h5 font-weight-bold">Available Spaces</h2>
            <p class="text-body-2 text-medium-emphasis mt-1">
              {{ filtereditems.length }} {{ filtereditems.length === 1 ? 'space' : 'spaces' }} available
            </p>
          </div>
          <div class="d-flex gap-2 align-center flex-wrap">
            <v-chip v-if="hasActiveFilters" variant="tonal" color="primary">
              <v-icon start>mdi-filter</v-icon>
              Filtered Results
            </v-chip>
            <v-btn
              color="success"
              to="/addnew"
              prepend-icon="mdi-plus"
            >
              Add Listing
            </v-btn>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Listings Grid -->
    <v-row v-if="filtereditems.length > 0">
      <MarketItem v-for="item in filtereditems" :key="item.id" :item="item" />
    </v-row>

    <!-- Loading Indicator -->
    <v-row v-if="loading" class="mt-4">
      <v-col cols="12" class="text-center">
        <v-progress-circular
          indeterminate
          color="primary"
          size="48"
        />
        <p class="text-body-2 text-medium-emphasis mt-2">Loading more spaces...</p>
      </v-col>
    </v-row>

    <!-- End of Results -->
    <v-row v-if="!hasMore && filtereditems.length > 0" class="mt-4">
      <v-col cols="12" class="text-center">
        <v-divider class="mb-4" />
        <p class="text-body-2 text-medium-emphasis">No more spaces to show</p>
      </v-col>
    </v-row>

    <!-- Empty State -->
    <v-row v-else>
      <v-col cols="12">
        <v-card variant="outlined" class="py-12">
          <v-card-text class="text-center">
            <v-icon size="64" color="grey-lighten-1" class="mb-4">
              mdi-magnify
            </v-icon>
            <h3 class="text-h6 mb-2">No Spaces Found</h3>
            <p class="text-body-2 text-medium-emphasis mb-4">
              {{ hasActiveFilters 
                ? 'Try adjusting your filters to see more results' 
                : 'No listings are currently available' 
              }}
            </p>
            <v-btn
              v-if="hasActiveFilters"
              variant="outlined"
              color="primary"
              @click="clearAllFilters"
            >
              Clear All Filters
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>

  <!-- Floating Action Button -->
  <v-btn
    color="success"
    size="large"
    icon
    elevation="8"
    to="/addnew"
    class="fab-add-listing"
  >
    <v-icon>mdi-plus</v-icon>
    <v-tooltip activator="parent" location="left">
      List Your Space
    </v-tooltip>
  </v-btn>

</template>

<script>
import { defineComponent } from 'vue';

// Components
import NavDrawer from '../components/NavDrawer.vue';
import MarketItem from '../components/MarketItem.vue';

export default defineComponent({
  name: 'HomeView',

  components: {
    NavDrawer,
    MarketItem,
  },

  created : function(){
    this.pageView();
    this.refreshView();
  },

  mounted() {
    window.addEventListener('scroll', this.handleScroll);
  },

  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
    if (this.filterDebounceTimer) {
      clearTimeout(this.filterDebounceTimer);
    }
  },

  data: () => ({
    drawer : false,
    items : [],
    filtereditems : [],
    filters : {
      cemeteryname : "",
      city : "",
      state : "",
      county : ""
    },
    lastQuery: {
      cemeteryname : "",
      city : "",
      state : "",
      county : ""
    },
    page : 0,
    pageSize: 20,
    currentOffset: 0,
    hasMore: true,
    loading: false,
    filterDebounceTimer: null,
  }),

  computed: {
    hasActiveFilters() {
      return this.filters.cemeteryname !== "" || 
             this.filters.city !== "" || 
             this.filters.state !== "" || 
             this.filters.county !== "";
    }
  },

  watch: {
    filters: {
      handler() {
        // Clear existing timer
        if (this.filterDebounceTimer) {
          clearTimeout(this.filterDebounceTimer);
        }
        
        // Set new timer - only trigger search after 1 second of inactivity
        this.filterDebounceTimer = setTimeout(() => {
          this.resetAndRefresh();
        }, 1000);
      },
      deep: true
    }
  },

  methods:{
    pageView(){
      fetch("https://app.cemeterycommunity.com:11000/api/pageview", { method: "POST", headers : { data : JSON.stringify( { group : 'Marketplace', page : 'Main' } ) } });

      if(this.$route.query.cemeteryname) this.filters.cemeteryname = this.$route.query.cemeteryname;
      if(this.$route.query.city) this.filters.city = this.$route.query.city;
      if(this.$route.query.state) this.filters.state = this.$route.query.state;
      if(this.$route.query.county) this.filters.county = this.$route.query.county;
      if(this.$route.query.page) this.page = this.$route.query.page;
    },
    clearAllFilters() {
      this.filters.cemeteryname = "";
      this.filters.city = "";
      this.filters.state = "";
      this.filters.county = "";
    },
    checkForFilter(){
      return ((this.filters.cemeteryname != "" || this.filters.city != "" || this.filters.state != "" || this.filters.county != "") && !this.filtereditems || this.filtereditems.length == 0)
    },
    resetAndRefresh() {
      this.currentOffset = 0;
      this.filtereditems = [];
      this.hasMore = true;
      this.refreshView();
    },
    refreshView(){
      if(this.loading || !this.hasMore) return;
      
      this.loading = true;
      
      const requestData = {
        limit: this.pageSize,
        offset: this.currentOffset,
        filters: {
          cemeteryname: this.filters.cemeteryname,
          city: this.filters.city,
          state: this.filters.state,
          county: this.filters.county
        }
      };

      // Store the query values
      this.lastQuery = {
        cemeteryname: this.filters.cemeteryname,
        city: this.filters.city,
        state: this.filters.state,
        county: this.filters.county
      };

      fetch("https://app.cemeterycommunity.com:11000/api/getmarketplaceitems", { 
        method: "POST",
        headers: { data: JSON.stringify(requestData) }
      })
      .then( res => res.json() )
      .then( res =>  {
        this.filtereditems = [...this.filtereditems, ...res.results];
        this.hasMore = res.hasMore || false;
        this.currentOffset += res.results.length;
        this.loading = false;
      })
      .catch(() => {
        this.loading = false;
      });
    },
    handleEnterKey() {
      // Check if any filter has a value and is different from last query
      const hasChanges = 
        (this.filters.cemeteryname !== "" && this.filters.cemeteryname !== this.lastQuery.cemeteryname) ||
        (this.filters.city !== "" && this.filters.city !== this.lastQuery.city) ||
        (this.filters.state !== "" && this.filters.state !== this.lastQuery.state) ||
        (this.filters.county !== "" && this.filters.county !== this.lastQuery.county);
      
      if (hasChanges) {
        // Clear the debounce timer and trigger search immediately
        if (this.filterDebounceTimer) {
          clearTimeout(this.filterDebounceTimer);
        }
        this.resetAndRefresh();
      }
    },
    handleScroll() {
      const scrollHeight = document.documentElement.scrollHeight;
      const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
      const clientHeight = document.documentElement.clientHeight;
      
      // Load more when within 500px of bottom
      if (scrollHeight - scrollTop - clientHeight < 500 && !this.loading && this.hasMore) {
        this.refreshView();
      }
    }
  }
});
</script>

<style scoped>
.fab-add-listing {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 100;
}

@media (max-width: 600px) {
  .fab-add-listing {
    bottom: 16px;
    right: 16px;
  }
}
</style>
